// The builder (DEVELOPMENT.md: Builder): drafts, the builder's state, and the core's builder
// answers (core/src/generate/builder.rs). The web app runs the report, suggestions, break-ins
// and Tidy in its Web Worker; Mark ready, Complete from here and Automatic run on the worker.
import type { ConstraintEntry } from './constraints';
import type { PuzzleDoc, Rc, ScoreBreakdown } from './puzzle';
import { shapedGrid, type ShapeKey } from './shapes';

export type HelpLevel = 'manual' | 'semi' | 'automatic';

/** What the builder keeps with a draft besides its document. */
export interface BuilderState {
  level: HelpLevel;
  /** Constraint types the app may add (plugin ids), and "given". */
  allowed: string[];
  /** Target score range, 0–10. */
  target: [number, number];
  /** Pieces the app added (everything else is the setter's own, locked): `pieceKey`s. */
  added: string[];
  /** The solution the app's suggestions hold on, kept between suggestions. */
  solution: string[] | null;
  /** The setter wrote the rules text (otherwise it is written from the constraints). */
  customRules: boolean;
  /** Came from the automatic mode and hasn't been edited since (marked "generated"). */
  generated: boolean;
  /** Suggestions the setter rejected, not to be offered again. */
  rejected: unknown[];
}

export const DEFAULT_BUILDER: BuilderState = {
  level: 'manual',
  allowed: ['given'],
  target: [3, 6],
  added: [],
  solution: null,
  customRules: false,
  generated: false,
  rejected: [],
};

/** Merges a stored state with the defaults (older or partial states). */
export function normalizeBuilder(v: unknown): BuilderState {
  const o = (v && typeof v === 'object' ? v : {}) as Partial<BuilderState>;
  const target =
    Array.isArray(o.target) &&
    o.target.length === 2 &&
    o.target.every((x) => typeof x === 'number' && x >= 0 && x <= 10) &&
    o.target[0] <= o.target[1]
      ? ([o.target[0], o.target[1]] as [number, number])
      : DEFAULT_BUILDER.target;
  return {
    level: o.level === 'semi' || o.level === 'automatic' ? o.level : 'manual',
    allowed: Array.isArray(o.allowed)
      ? o.allowed.filter((x) => typeof x === 'string')
      : DEFAULT_BUILDER.allowed,
    target,
    added: Array.isArray(o.added) ? o.added.filter((x) => typeof x === 'string') : [],
    solution:
      Array.isArray(o.solution) && o.solution.every((x) => typeof x === 'string')
        ? o.solution
        : null,
    customRules: o.customRules === true,
    generated: o.generated === true,
    rejected: Array.isArray(o.rejected) ? o.rejected.slice(-200) : [],
  };
}

/** A stable key for a document piece: a constraint entry or a given's cell. */
export function pieceKey(piece: { entry: ConstraintEntry } | { given: Rc }): string {
  if ('given' in piece) return `given:${piece.given[0]},${piece.given[1]}`;
  return `entry:${canonical(piece.entry)}`;
}

/** JSON with sorted keys, so the same entry always has the same key. */
export function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .sort()
      .filter((k) => o[k] !== undefined)
      .map((k) => `${JSON.stringify(k)}:${canonical(o[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v);
}

/** A row of the Build tab. */
export interface DraftSummary {
  id: string;
  title: string;
  status: 'draft' | 'ready';
  rows: number;
  cols: number;
  constraintTypes: string[];
  score: number | null;
  /** For the thumbnail (without its solution). */
  document: PuzzleDoc;
  updatedAt: string;
}

export interface BuildsResponse {
  drafts: DraftSummary[];
  ready: DraftSummary[];
}

export interface Draft {
  id: string;
  title: string;
  status: 'draft' | 'ready';
  document: PuzzleDoc;
  builder: BuilderState;
  score: number | null;
  updatedAt: string;
}

export interface NewDraftRequest {
  /** Grid size n (n×n, digits 1–n), 4 to 16. */
  size: number;
  /** A shape made of size×size grids (samurai and the like) instead of one grid. */
  shape?: ShapeKey | null;
  /** Jigsaw regions drawn by the setter instead of boxes. */
  irregular?: boolean;
  title?: string;
  /** A finished document (from the automatic mode, or a copy of a ready puzzle). */
  document?: PuzzleDoc;
  builder?: Partial<BuilderState>;
}

export interface NewDraftResponse {
  id: string;
}

export interface SaveDraftRequest {
  title: string;
  document: PuzzleDoc;
  builder: BuilderState;
}

export interface SaveDraftResponse {
  updatedAt: string;
}

/** A worker job the builder waits on (Mark ready, Complete, Automatic). */
export interface BuildJobResponse {
  jobId: string;
}

export interface BuildJobStatus<T = unknown> {
  state: 'queued' | 'running' | 'done' | 'failed' | 'paused';
  progress: number;
  error: string | null;
  result: T | null;
}

export interface CompleteJobRequest {
  document: PuzzleDoc;
  types: string[];
  minScore: number;
  maxScore: number;
  solution: string[] | null;
}

/** core `complete`: the document with the app's additions. */
export interface CompleteResult {
  doc: PuzzleDoc;
  score: ScoreBreakdown;
  /** Indices into `doc.constraints` of the entries the app added. */
  addedConstraints: number[];
  addedGivens: Rc[];
  inRange: boolean;
}

export interface AutoJobRequest {
  size: number;
  irregular: boolean;
  /** Constraint types to place (none: a classic). */
  types: string[];
  /** Global rules (anti-knight, anti-king, non-consecutive, diagonals). */
  global: string[];
  minScore: number;
  maxScore: number;
  /** Givens in rotationally symmetric pairs. */
  symmetry: boolean;
}

export interface AutoCandidate {
  doc: PuzzleDoc;
  score: ScoreBreakdown;
  quality: number;
  inRange: boolean;
}

export interface AutoResult {
  candidates: AutoCandidate[];
}

export interface ReadyResult {
  puzzleId: string;
  score: number;
}

// The core's builder answers (core/src/generate/builder.rs).

export interface PathStep {
  technique: string;
  /** "Hidden single", or the rule's name for a rule's own step. */
  name: string;
  text: string;
  cells: Rc[];
  placed: [Rc, string][];
  removed: number;
  cost: number;
}

export interface Stall {
  step: number;
  cell: Rc;
  /** "box 5". */
  house: string;
  area: Rc[];
  open: number;
}

export interface BuildReport {
  solverVersion: string;
  unsupported: string[];
  /** 0, 1 or 2 (two means "more than one"). */
  solutionCount: number;
  complete: boolean;
  solution: string[] | null;
  conflicts: { cells: Rc[]; rule: string }[];
  trueCandidates: string[][] | null;
  steps: PathStep[];
  solved: boolean;
  stall: Stall | null;
  score: ScoreBreakdown | null;
  rules: string;
}

export interface AssistRequest {
  doc: PuzzleDoc;
  types: string[];
  minScore: number;
  maxScore: number;
  solution: string[] | null;
  seed: number;
  exclude: unknown[];
}

export interface Suggestion {
  entry: ConstraintEntry | null;
  given: { cell: Rc; value: string } | null;
  label: string;
  cells: Rc[];
  scoreBefore: number | null;
  scoreAfter: number | null;
  moreSteps: number;
  /** Candidates it removes where the logic stalls. */
  removes: number;
  unique: boolean;
  target: 'in' | 'above' | 'below' | 'open';
  opens: string | null;
}

export interface Assist {
  solution: string[] | null;
  stall: Stall | null;
  suggestions: Suggestion[];
  note: string | null;
}

// The builder's fog (core/src/generate/builder/fog.rs).

export interface FogRequest {
  doc: PuzzleDoc;
  /** How long the solve through the fog may take (20 by default). */
  seconds?: number;
}

/** One step of the solve through the fog, for stepping through it. */
export interface FogStepInfo {
  name: string;
  cells: Rc[];
  placed: [Rc, string][];
}

/** Where the solve through the fog stops: the next step with everything in view, and the rules
 * it uses that are still in the fog. */
export interface FogStop {
  cells: Rc[];
  text: string;
  fogged: string[];
}

export interface DraftFogReport {
  /** It solves through its fog: soundly, every hidden given lit before anything needs it. */
  solved: boolean;
  steps: FogStepInfo[];
  cells: number;
  startLit: number;
  lit: number;
  /** Per cell (core order): how many steps before it lit, 0 from the start; null never. */
  litAt: (number | null)[];
  score: ScoreBreakdown | null;
  stop: FogStop | null;
  hiddenGivens: Rc[];
  hiddenGivensHold: boolean;
  unsound: string | null;
  timedOut: boolean;
  /** Why there is no solve to show (not one solution yet). */
  note: string | null;
}

export interface ShrunkLight {
  visible: Rc[];
  removed: number;
  timedOut: boolean;
}

export interface TidyRequest {
  doc: PuzzleDoc;
  /** Indices into `doc.constraints` that may go. */
  constraints: number[];
  givens: Rc[];
}

export interface Tidied {
  doc: PuzzleDoc;
  removedConstraints: number[];
  removedGivens: Rc[];
  scoreBefore: number | null;
  scoreAfter: number | null;
}

/** Box shape for an n×n grid, as the core picks it: 4→2×2, 6→2×3, 9→3×3, 12→3×4, 16→4×4;
 * prime sizes get none ([0, 0]). */
export function defaultBoxes(n: number): [number, number] {
  let best: [number, number] = [0, 0];
  for (let r = 2; r * r <= n; r++) {
    if (n % r === 0) best = [r, n / r];
  }
  return best;
}

/** An empty n×n grid with digits 1–n: boxes, or jigsaw regions to redraw (starting from the
 * boxes, or from the rows where the size has no boxes); or an empty shape of size×size
 * grids. */
export function blankDoc(
  size: number,
  irregular = false,
  title = '',
  shape: ShapeKey | null = null,
): PuzzleDoc {
  if (shape) {
    return {
      format: 'zinc-puzzle',
      version: 1,
      meta: { title, author: '', rules: 'Normal sudoku rules apply.', finishMessage: null },
      grid: shapedGrid(shape, size),
      givens: [],
      constraints: [],
      cosmetics: [],
      solution: null,
    };
  }
  const [br, bc] = defaultBoxes(size);
  const digits = Array.from({ length: size }, (_, i) => String(i + 1));
  const jigsaw = irregular || br === 0;
  let regions: Rc[][] | null = null;
  if (jigsaw) {
    regions = Array.from({ length: size }, () => [] as Rc[]);
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        const k = br > 0 ? Math.floor(r / br) * (size / bc) + Math.floor(c / bc) : r;
        regions[k]!.push([r, c]);
      }
    }
  }
  return {
    format: 'zinc-puzzle',
    version: 1,
    meta: { title, author: '', rules: 'Normal sudoku rules apply.', finishMessage: null },
    grid: {
      rows: size,
      cols: size,
      cells: null,
      regions,
      boxRows: jigsaw ? 0 : br,
      boxCols: jigsaw ? 0 : bc,
      digits,
      subGrids: null,
    },
    givens: [],
    constraints: [],
    cosmetics: [],
    solution: null,
    fog: null,
  };
}
